/*eslint-disable max-lines*/
import {
  useLocationAlarmUpdates,
  useResolveLocationAlarmIncident,
  useSendActionToZapier,
  useSendScanActionToZapier,
  useUpdateAlarmGroupState,
  useUpdateLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  AIRecommendation,
  Escalation,
  Location,
  LocationAlarmUpdateType,
  ScanAlarmUpdateType,
  StatusType,
  UnifiedMonitoringProduct,
  ZapierActionPayload,
} from '@hakimo-ui/hakimo/types';
import { Select, SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import {
  eventTracker,
  INCIDENT_CATEGORIES,
  isAlreadyResolvedIncidentError,
  isUnifiedDataVersion,
  RESOLUTION_REASONS,
  toast,
  useCanUpdateAlarmGroupStatus,
  useCanUpdateLocationAlarmStatus,
  useUser,
} from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  Modal,
  Selectable,
  Textarea,
} from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import { useCallback, useRef, useState } from 'react';
import { useAlarmGroupStatic } from '../alarm-provider/AlarmGroupProvider';
import {
  DispatchTimelineEditor,
  DispatchTimelineEditorHandle,
} from './DispatchTimelineEditor';

interface Props {
  alarmId: string;
  escalation: Escalation;
  location: Location;
  onStatusChange?: (status: StatusType) => void;
  onClose: () => void;
  product: UnifiedMonitoringProduct;
  addTemporaryResolvedState?: (alarmGroupId: string) => void;
  cameraNames?: string[];
  onResolveEscalation?: (
    comment: string,
    reason?: string,
    category?: string
  ) => void;
  aiOperatorRep?: AIRecommendation;
}

export function ResolveEscalationModal(props: Props) {
  const {
    alarmId,
    escalation,
    location,
    onClose,
    onStatusChange,
    product,
    addTemporaryResolvedState,
    cameraNames = [],
    onResolveEscalation,
    aiOperatorRep,
  } = props;
  const [textVal, setTextVal] = useState('');
  const [resolutionReason, setResolutionReason] = useState<
    Selectable | undefined
  >();
  const [incidentCategory, setIncidentCategory] = useState<
    Selectable | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTimelineEditing, setIsTimelineEditing] = useState(false);
  const timelineEditorRef = useRef<DispatchTimelineEditorHandle>(null);
  const { dataVersion } = useAlarmGroupStatic();

  const { data: alarmUpdates } = useLocationAlarmUpdates(
    alarmId,
    0,
    escalation.incidentStartTimeUtc,
    product,
    undefined,
    true,
    dataVersion
  );

  const timelineEvents = (alarmUpdates?.items ?? []).filter(
    (u) =>
      u.update_type === LocationAlarmUpdateType.DISPATCH ||
      u.update_type === ScanAlarmUpdateType.DISPATCH ||
      u.update_type === LocationAlarmUpdateType.ARRIVAL ||
      u.update_type === ScanAlarmUpdateType.ARRIVAL ||
      u.update_type === LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT ||
      u.update_type === ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT
  );

  const user = useUser();
  const canUpdateLocationAlarmStatus = useCanUpdateLocationAlarmStatus();
  const canUpdateAlarmGroupStatus = useCanUpdateAlarmGroupStatus();
  const { mutate: sendActionToZapierMutation, isLoading: isSendingToZapier } =
    useSendActionToZapier(undefined, undefined, dataVersion);
  const { mutate: sendScanActionToZapier, isLoading: isSendingScanToZapier } =
    useSendScanActionToZapier();

  const onSuccess = () => {
    onStatusChange && onStatusChange(StatusType.RESOLVED);
    if (addTemporaryResolvedState) {
      addTemporaryResolvedState(alarmId);
    }
    onResolveEscalation?.(textVal);
    onClose();
  };

  const resolveAlarmMutation = useUpdateLocationAlarm(alarmId, {
    onSuccess,
    dataVersion,
  });
  // v3 only, and only for the already-closed case: source-group scoping means
  // several alarms share one incident, so two operators on sibling alarms race
  // routinely. That is not a system failure and should not read as one. Any
  // other error, and every v2 error, falls through to the generic alert below
  // so legacy behaviour is unchanged.
  const onResolveIncidentError = (err: Error) => {
    if (
      isUnifiedDataVersion(dataVersion) &&
      isAlreadyResolvedIncidentError(err)
    ) {
      toast(
        'This escalation has already been resolved, most likely from another alarm in the same group.',
        { type: 'warning' }
      );
    }
  };
  const resolveIncidentMutation = useResolveLocationAlarmIncident(
    undefined,
    onResolveIncidentError,
    dataVersion
  );
  const resolveAlarmGroupMutation = useUpdateAlarmGroupState(
    alarmId,
    onSuccess
  );

  const isAlreadyResolved =
    isUnifiedDataVersion(dataVersion) &&
    isAlreadyResolvedIncidentError(resolveIncidentMutation.error ?? undefined);

  const onTextChange = (val: string) => setTextVal(val);
  const getIncidentCategories = useCallback(
    (query: string) =>
      INCIDENT_CATEGORIES.filter((category) =>
        category.name.toLowerCase().includes(query.toLowerCase())
      ),
    []
  );
  const resolutionReasonId = resolutionReason?.id;
  const incidentCategoryId = incidentCategory?.id;

  const onSubmit = async () => {
    const reason = resolutionReasonId ?? '';
    const category = incidentCategoryId ?? '';

    if (product === UnifiedMonitoringProduct.SCAN) {
      if (onResolveEscalation) {
        onResolveEscalation(textVal, reason, category);
        onClose();
        return;
      }

      const resolveAlarmGroupPayload = {
        resolution: 'escalation_close' as const,
        resolutionComment: textVal,
        resolutionReason: reason,
        resolutionIncidentCategory: category,
        operatorId: user.email,
      };
      resolveAlarmGroupMutation.mutate(resolveAlarmGroupPayload);
    } else {
      const locationIncidentPayload = {
        incident_id: parseInt(escalation.id),
        resolved_by: user.id,
        resolution_comment: textVal,
        location_alarm_id: alarmId,
        resolution_reason: resolutionReasonId,
        incident_category: incidentCategoryId,
      };
      const commentText = [
        'Escalation closed',
        incidentCategoryId ? `Incident Category: ${incidentCategoryId}` : '',
        resolutionReasonId ? `Resolution Reason: ${resolutionReasonId}` : '',
        textVal ? `Resolved Comment: ${textVal}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      const resolveAlarmPayload = {
        status: StatusType.RESOLVED,
        comment: commentText,
        incident_category: incidentCategoryId,
        resolution_reason: resolutionReasonId,
      };

      // Sequenced, not parallel: resolving the incident first means a failure
      // leaves the alarm unresolved, so the banner and its Resolve control stay
      // on screen. In parallel the alarm could resolve while the incident
      // stayed open, with no UI path back to it.
      try {
        await resolveIncidentMutation.mutateAsync(locationIncidentPayload);
      } catch (err) {
        // The already-resolved race is not a failure: the incident is already
        // in the state we wanted (closed by whoever worked a sibling alarm), so
        // carry on and resolve this alarm. Anything else stops here, surfaced
        // by onResolveIncidentError or the generic alert below.
        const alreadyResolved =
          isUnifiedDataVersion(dataVersion) &&
          isAlreadyResolvedIncidentError(
            err instanceof Error ? err : undefined
          );
        if (!alreadyResolved) {
          return;
        }
      }

      resolveAlarmMutation.mutate(resolveAlarmPayload);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await timelineEditorRef.current?.flushUpdates();
    } catch {
      toast('Failed to save timeline edits. Please try again.', {
        type: 'error',
      });
      setIsSubmitting(false);
      return;
    }

    if (product === UnifiedMonitoringProduct.SCAN) {
      const cameraNamesArr = [...new Set(cameraNames)];
      const startTime = escalation.incidentStartTimeUtc;
      const zapierPayload = {
        hakimo_dispatcher_name: user.name,
        alarm_group_id: alarmId,
        premise: location.name,
        location_id: location.id,
        incident_date: dayjs(startTime).tz('Asia/Kolkata').format('YYYY-MM-DD'),
        reported_time: dayjs(startTime).tz('Asia/Kolkata').format('hh:mm:ss A'),
        resolution_time: dayjs().tz('Asia/Kolkata').format('hh:mm:ss A'),
        operator_email: user.email,
        camera_names: cameraNamesArr.join(', '),
        incident_category: aiOperatorRep?.headline || '',
        incidentCategory: incidentCategoryId || '',
        resolution_comment: textVal,
        resolution_reason: resolutionReasonId || '',
        timestamp: new Date().toUTCString(),
        alarm_group_link: `${window.location.origin}/alarm-groups/${alarmId}`,
        time_1st_call: '',
        time_2nd_call: '',
        time_3rd_call: '',
      };
      sendScanActionToZapier(zapierPayload);
      await onSubmit();
      setIsSubmitting(false);
      return;
    }
    const startTime = dayjs(escalation.incidentStartTimeUtc).tz(
      location.timezone
    );
    const partitionKey = dayjs
      .utc(escalation.incidentStartTimeUtc)
      .format('YYYYMM');
    const endTime = dayjs().tz(location.timezone);
    const zapierPayload: ZapierActionPayload = {
      alarm_id: alarmId,
      site_name: location.name,
      alarm_date: startTime.format('YYYY-MM-DD'),
      alarm_time: startTime.format('HH:mm:ss'),
      alarm_resolve_date: endTime.format('YYYY-MM-DD'),
      alarm_resolve_time: endTime.format('HH:mm:ss'),
      location_description: location.description,
      operator_email: user.email,
      location_incident_id: escalation.id,
      relevant_cameras: cameraNames.join(', '),
      timezone: location.timezone,
      timestamp: new Date()
        .toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour12: false })
        .replace(',', ''),
      videoPath: `${window.location.origin}/location-alarms/${alarmId}?partitionKey=${partitionKey}`,
      alarmType: '',
      dispatchType: '',
      dispatchAgencyName: '',
      dispatcherName_ID: '',
      dispatch_cancelled: '',
      disposition: resolutionReasonId,
      incident_category: incidentCategoryId,
      incident_type: aiOperatorRep?.headline || '',
      operator_id: user.name,
      PD_call_reference: '',
      resolution_comment: textVal,
      talkdown_used: '',
      soc: user.tenant.name,
    };
    sendActionToZapierMutation(zapierPayload);
    await onSubmit();
    setIsSubmitting(false);
  };

  const canUpdateStatus =
    product === UnifiedMonitoringProduct.SCAN
      ? canUpdateAlarmGroupStatus
      : canUpdateLocationAlarmStatus;

  const isLoading =
    isSubmitting ||
    isSendingToZapier ||
    isSendingScanToZapier ||
    (product === UnifiedMonitoringProduct.SCAN
      ? resolveAlarmGroupMutation.isLoading
      : resolveAlarmMutation.isLoading || resolveIncidentMutation.isLoading);

  return (
    <Modal
      title="Resolve Incident"
      onClose={onClose}
      open
      closable
      footer={[
        canUpdateStatus && (
          <Button
            key="resolve"
            title="Resolve Incident"
            variant="primary"
            disabled={
              textVal === '' ||
              !resolutionReason ||
              !incidentCategory ||
              isLoading ||
              isTimelineEditing
            }
            onClick={handleSubmit}
            loading={isLoading}
            onSideEffect={eventTracker(
              'close_and_resolve_active_location_incident'
            )}
          >
            Resolve
          </Button>
        ),
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
      ]}
    >
      <div className="w-full space-y-2 px-8 py-4 sm:w-[56rem] sm:max-w-5xl">
        {(product === UnifiedMonitoringProduct.SCAN
          ? resolveAlarmGroupMutation.isError
          : resolveAlarmMutation.isError ||
            // Already-resolved is surfaced as its own warning toast above, so
            // it must not also raise the generic failure alert.
            (resolveIncidentMutation.isError && !isAlreadyResolved)) && (
          <Alert type="error">
            Error submitting and resolving the incident
          </Alert>
        )}
        <Alert type="info">
          Alarm should only be resolved once site POC and PD are alerted about
          the incident.
        </Alert>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-1">
            <div className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold tracking-wide">
              Incident Category
            </div>
            <Select
              value={incidentCategory}
              placeholder="Search incident category"
              getItems={getIncidentCategories}
              displayValue={(item) => item?.name ?? ''}
              onChange={(value) => setIncidentCategory(value ?? undefined)}
              id={(item) => item?.id || ''}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold tracking-wide">
              Resolution Reason
            </div>
            <SelectMenu
              value={resolutionReason}
              placeholder="Select a reason"
              items={RESOLUTION_REASONS}
              displayValue={(item) => item?.name ?? ''}
              onChange={setResolutionReason}
              id={(item) => item?.id || ''}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold tracking-wide">
            Comment
          </div>
          <Textarea
            value={textVal}
            rows={2}
            minRows={2}
            placeholder="Describe the incident and the actions taken"
            onChange={onTextChange}
          />
        </div>

        <div className="border-onlight-line-2 dark:border-ondark-line-2 border-b pt-2" />

        <DispatchTimelineEditor
          ref={timelineEditorRef}
          events={timelineEvents}
          timezone={location.timezone}
          alarmId={alarmId}
          product={product}
          onEditingChange={(editing) => setIsTimelineEditing(editing)}
        />
      </div>
    </Modal>
  );
}
export default ResolveEscalationModal;
